// OscarGuidancePanel — Pass 7 / O2.
//
// Operator-curated general guidance management surface. CRUD over the
// oscar_general_guidance collection. All write actions go through the
// operator-tier-gated endpoints established in Pass 6.
//
// Layout:
//   - Header with category filter
//   - "Add new entry" inline form
//   - List of existing entries (active by default; toggle to show inactive)
//   - Each entry row: inline edit of category/title/body, soft-delete,
//     reactivate
//
// Trust posture surfaced in the UI: the page header explicitly notes
// that guidance entries are CONTEXT, not host-confirmed facts. KB
// remains the authoritative source.

import React, { useEffect, useState } from 'react';
import { Layers, Plus, Trash2, RotateCcw, Save, X, AlertCircle } from 'lucide-react';
import {
  getOscarGuidance, getOscarGuidanceCategories,
  createOscarGuidance, updateOscarGuidance, deleteOscarGuidance,
} from '../../data/api';

// Visual tokens — match Oscar admin surface (AITab + MessageTemplatesTab).
// Pass 8 / M21 — aligned to the canonical AI palette established in
// AITab.jsx + MiloConsolePanel.jsx. SURFACE_WARM derives from the
// canonical GOLD's RGB (198,167,90).
const GOLD    = '#C6A75A';
const GOLD_DK = '#8E7A2C';
const DARK    = '#1B1812';
const MUTED   = '#5C5C66';
const BORDER  = 'rgba(0,0,0,0.10)';
const SURFACE = '#FFFFFF';
const SURFACE_WARM = 'rgba(198,167,90,0.05)';

// Corrective pre-apply pass — premium card / glassmorphic helpers used
// across the AI admin surface. Same tokens as MiloBoundariesPanel.
const aiCardStyle = {
  background: SURFACE,
  border: `1px solid ${BORDER}`,
  borderRadius: 14,
  padding: 22,
  boxShadow: '0 1px 0 rgba(198,167,90,0.06) inset, 0 18px 36px -22px rgba(27,24,18,0.10)',
  position: 'relative',
};

const aiCardAccentStripe = {
  position: 'absolute',
  top: -1, left: 22, right: 22, height: 2,
  background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`,
  borderRadius: 2,
  opacity: 0.55,
};

const Badge = ({ children, tone = 'neutral' }) => {
  const palette = (tone === 'active')
    ? { bg: 'rgba(34,197,94,0.12)', fg: '#15803d', br: 'rgba(34,197,94,0.30)' }
    : (tone === 'inactive')
    ? { bg: 'rgba(0,0,0,0.05)', fg: MUTED, br: 'rgba(0,0,0,0.15)' }
    : { bg: 'rgba(198,167,90,0.10)', fg: GOLD_DK, br: 'rgba(198,167,90,0.30)' };
  return (
    <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.6px',
      padding: '2px 8px', borderRadius: 99, textTransform: 'uppercase',
      background: palette.bg, color: palette.fg,
      border: `1px solid ${palette.br}` }}>
      {children}
    </span>
  );
};

const OscarGuidancePanel = () => {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [newEntry, setNewEntry] = useState({ category: 'gift', title: '', body: '' });

  const refresh = () => {
    const params = {};
    if (filterCategory) params.category = filterCategory;
    if (includeInactive) params.include_inactive = true;
    getOscarGuidance(params)
      .then(d => { setEntries(d.entries || []); setError(null); })
      .catch(err => setError(err?.response?.data?.detail || err.message));
  };

  useEffect(() => {
    getOscarGuidanceCategories()
      .then(d => setCategories(d.suggested || []))
      .catch(() => {});
  }, []);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterCategory, includeInactive]);

  const handleCreate = async () => {
    const t = (newEntry.title || '').trim();
    const b = (newEntry.body || '').trim();
    if (!t || !b) {
      alert('Title and body are required.');
      return;
    }
    setSavingId('__new__');
    try {
      await createOscarGuidance({
        category: newEntry.category || 'general',
        title: t,
        body: b,
        is_active: true,
      });
      setNewEntry({ category: 'gift', title: '', body: '' });
      setShowCreate(false);
      refresh();
    } catch (err) {
      alert(`Could not create entry: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const handleStartEdit = (e) => {
    setEditingId(e.id);
    setEditDraft({ category: e.category, title: e.title, body: e.body });
  };
  const handleCancelEdit = () => { setEditingId(null); setEditDraft({}); };
  const handleSaveEdit = async (id) => {
    setSavingId(id);
    try {
      await updateOscarGuidance(id, editDraft);
      setEditingId(null);
      setEditDraft({});
      refresh();
    } catch (err) {
      alert(`Could not save: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm('Soft-delete this guidance entry? You can reactivate later by toggling Show Inactive.')) return;
    setSavingId(id);
    try {
      await deleteOscarGuidance(id);
      refresh();
    } catch (err) {
      alert(`Could not delete: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const handleReactivate = async (id) => {
    setSavingId(id);
    try {
      await updateOscarGuidance(id, { is_active: true });
      refresh();
    } catch (err) {
      alert(`Could not reactivate: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div data-testid="oscar-subtab-panel-guidance"
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header + trust-posture note — Corrective visual uplift */}
      <div style={aiCardStyle}>
        <div style={aiCardAccentStripe} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
          {/* Circle outline (replaces flat square) — luxe AI identity */}
          <div style={{ width: 38, height: 38, borderRadius: '50%',
            background: SURFACE,
            border: `1.5px solid ${GOLD}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 0 4px rgba(198,167,90,0.06)' }}>
            <Layers size={17} color={GOLD_DK} strokeWidth={1.75} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
              fontSize: 19, fontWeight: 600, color: DARK,
              letterSpacing: '0.2px', lineHeight: 1.15 }}>
              General Guidance
            </div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 3,
              letterSpacing: '0.1px', lineHeight: 1.45 }}>
              Operator-curated context for travel, lodging, gifts, and other common topics.
            </div>
          </div>
        </div>
        {/* Trust note — gold left-edge accent for premium AI identity */}
        <div data-testid="guidance-trust-note"
          style={{ display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '12px 14px 12px 16px', borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(198,167,90,0.06) 0%, rgba(198,167,90,0.02) 100%)',
            border: '1px solid rgba(198,167,90,0.20)',
            borderLeft: `3px solid ${GOLD}`,
            position: 'relative' }}>
          <AlertCircle size={15} color={GOLD_DK} strokeWidth={1.75}
            style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: DARK, lineHeight: 1.6 }}>
            <strong style={{ color: GOLD_DK }}>Guidance is context, not confirmed fact.</strong>
            {' '}Oscar uses entries here as supplementary grounding when the Knowledge Base
            has no host-confirmed answer. KB facts always take precedence.
            For yes/no questions that depend on family confirmation, Oscar
            will defer rather than treat guidance as final.
          </div>
        </div>
      </div>

      {/* Filter + Add toolbar — Corrective visual uplift */}
      <div style={{ ...aiCardStyle, padding: 16, display: 'flex',
        alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8,
          fontSize: 11, color: MUTED, fontWeight: 600,
          letterSpacing: '0.3px', textTransform: 'uppercase' }}>
          Category
          <select
            data-testid="guidance-filter-category"
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            style={{ padding: '6px 10px', borderRadius: 6,
              border: `1px solid ${BORDER}`, background: SURFACE,
              fontSize: 12, color: DARK, fontWeight: 500,
              cursor: 'pointer',
              outline: 'none' }}>
            <option value="">All categories</option>
            {categories.map(c => (<option key={c} value={c}>{c}</option>))}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7,
          fontSize: 11, color: MUTED, fontWeight: 600, cursor: 'pointer',
          letterSpacing: '0.3px', textTransform: 'uppercase' }}>
          <input type="checkbox"
            data-testid="guidance-show-inactive"
            checked={includeInactive}
            onChange={e => setIncludeInactive(e.target.checked)}
            style={{ accentColor: GOLD, cursor: 'pointer',
              width: 14, height: 14 }} />
          Show inactive
        </label>
        <div style={{ marginLeft: 'auto' }}>
          <button
            data-testid="guidance-add-button"
            onClick={() => setShowCreate(s => !s)}
            style={{ display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: showCreate
                ? SURFACE
                : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DK} 100%)`,
              color: showCreate ? GOLD_DK : '#FFFFFF',
              fontSize: 12, fontWeight: 700,
              letterSpacing: '0.4px', textTransform: 'uppercase',
              cursor: 'pointer',
              boxShadow: showCreate
                ? `inset 0 0 0 1.5px ${GOLD}`
                : '0 4px 12px -4px rgba(198,167,90,0.45)',
              transition: 'all 180ms ease-out' }}>
            <Plus size={14} strokeWidth={2.25} />
            {showCreate ? 'Cancel' : 'New entry'}
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div data-testid="guidance-create-form"
          style={{ ...aiCardStyle, padding: 22, display: 'flex',
            flexDirection: 'column', gap: 14 }}>
          <div style={aiCardAccentStripe} />
          {/* Form-section label */}
          <div style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 16, fontWeight: 600, color: DARK,
            letterSpacing: '0.2px', marginBottom: 2 }}>
            New guidance entry
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6,
              fontSize: 10, color: MUTED, fontWeight: 700, letterSpacing: '0.5px',
              textTransform: 'uppercase', minWidth: 180 }}>
              Category
              <input
                data-testid="guidance-new-category"
                type="text" list="guidance-cat-list"
                value={newEntry.category}
                onChange={e => setNewEntry(s => ({ ...s, category: e.target.value }))}
                style={{ padding: '8px 12px', borderRadius: 8,
                  border: `1px solid ${BORDER}`, background: SURFACE,
                  fontSize: 12, color: DARK, fontWeight: 500,
                  outline: 'none',
                  transition: 'border-color 150ms, box-shadow 150ms',
                  boxShadow: '0 1px 0 rgba(0,0,0,0.02) inset' }} />
              <datalist id="guidance-cat-list">
                {categories.map(c => (<option key={c} value={c} />))}
              </datalist>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6,
              fontSize: 10, color: MUTED, fontWeight: 700, letterSpacing: '0.5px',
              textTransform: 'uppercase', flex: 1, minWidth: 240 }}>
              Title
              <input
                data-testid="guidance-new-title"
                type="text"
                value={newEntry.title}
                onChange={e => setNewEntry(s => ({ ...s, title: e.target.value }))}
                placeholder="Short, specific (e.g. Recommended hotels near the venue)"
                style={{ padding: '8px 12px', borderRadius: 8,
                  border: `1px solid ${BORDER}`, background: SURFACE,
                  fontSize: 12, color: DARK, fontWeight: 500,
                  outline: 'none',
                  transition: 'border-color 150ms, box-shadow 150ms',
                  boxShadow: '0 1px 0 rgba(0,0,0,0.02) inset' }} />
            </label>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6,
            fontSize: 10, color: MUTED, fontWeight: 700, letterSpacing: '0.5px',
            textTransform: 'uppercase' }}>
            Body
            <textarea
              data-testid="guidance-new-body"
              value={newEntry.body}
              onChange={e => setNewEntry(s => ({ ...s, body: e.target.value }))}
              rows={4}
              placeholder="2–4 sentences. Operator-curated context, not a host-confirmed fact."
              style={{ padding: '10px 12px', borderRadius: 8,
                border: `1px solid ${BORDER}`, background: SURFACE,
                fontSize: 12, color: DARK, fontFamily: 'inherit',
                fontWeight: 400, lineHeight: 1.55,
                resize: 'vertical',
                outline: 'none',
                transition: 'border-color 150ms, box-shadow 150ms',
                boxShadow: '0 1px 0 rgba(0,0,0,0.02) inset' }} />
          </label>
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              data-testid="guidance-create-submit"
              onClick={handleCreate}
              disabled={savingId === '__new__'}
              style={{ padding: '9px 18px', borderRadius: 8, border: 'none',
                background: savingId === '__new__'
                  ? 'rgba(198,167,90,0.45)'
                  : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DK} 100%)`,
                color: '#FFFFFF', fontSize: 12, fontWeight: 700,
                letterSpacing: '0.4px', textTransform: 'uppercase',
                cursor: savingId === '__new__' ? 'wait' : 'pointer',
                boxShadow: savingId === '__new__'
                  ? 'none'
                  : '0 4px 12px -4px rgba(198,167,90,0.45)',
                transition: 'all 180ms ease-out' }}>
              {savingId === '__new__' ? 'Saving…' : 'Create entry'}
            </button>
          </div>
        </div>
      )}

      {/* Error / empty / list */}
      {error && (
        <div data-testid="guidance-error"
          style={{ padding: 12, color: '#b91c1c',
            background: 'rgba(239,68,68,0.06)',
            border: '1px solid rgba(239,68,68,0.20)',
            borderRadius: 8, fontSize: 12 }}>
          <strong>Could not load guidance:</strong> {error}
        </div>
      )}
      {!error && entries.length === 0 && (
        <div data-testid="guidance-empty"
          style={{ padding: 20, color: MUTED, fontSize: 12,
            fontStyle: 'italic', textAlign: 'center',
            background: SURFACE, border: `1px solid ${BORDER}`,
            borderRadius: 12 }}>
          {filterCategory || includeInactive
            ? 'No guidance entries match the current filter.'
            : 'No guidance entries yet. Click "New entry" to create your first one.'}
        </div>
      )}
      {entries.map(e => (
        <div key={e.id}
          data-testid={`guidance-entry-${e.id}`}
          style={{ ...aiCardStyle, padding: 18,
            opacity: e.is_active ? 1 : 0.62,
            display: 'flex', flexDirection: 'column', gap: 10,
            transition: 'opacity 180ms ease-out, transform 180ms ease-out' }}>
          {editingId === e.id ? (
            // ── Inline edit ─────────────────────────────────────────
            <>
              <div style={{ fontSize: 9, fontWeight: 800,
                letterSpacing: '0.5px', textTransform: 'uppercase',
                color: GOLD_DK, marginBottom: 2 }}>
                Editing
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <input
                  data-testid={`guidance-edit-category-${e.id}`}
                  type="text" list="guidance-cat-list"
                  value={editDraft.category || ''}
                  onChange={ev => setEditDraft(s => ({ ...s, category: ev.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: 8,
                    border: `1px solid ${BORDER}`, background: SURFACE,
                    fontSize: 12, color: DARK, fontWeight: 500,
                    minWidth: 160, outline: 'none',
                    boxShadow: '0 1px 0 rgba(0,0,0,0.02) inset' }} />
                <input
                  data-testid={`guidance-edit-title-${e.id}`}
                  type="text"
                  value={editDraft.title || ''}
                  onChange={ev => setEditDraft(s => ({ ...s, title: ev.target.value }))}
                  style={{ padding: '8px 12px', borderRadius: 8,
                    border: `1px solid ${BORDER}`, background: SURFACE,
                    fontSize: 12, color: DARK, fontWeight: 500,
                    flex: 1, minWidth: 220, outline: 'none',
                    boxShadow: '0 1px 0 rgba(0,0,0,0.02) inset' }} />
              </div>
              <textarea
                data-testid={`guidance-edit-body-${e.id}`}
                value={editDraft.body || ''}
                onChange={ev => setEditDraft(s => ({ ...s, body: ev.target.value }))}
                rows={4}
                style={{ padding: '10px 12px', borderRadius: 8,
                  border: `1px solid ${BORDER}`, background: SURFACE,
                  fontSize: 12, color: DARK, fontFamily: 'inherit',
                  fontWeight: 400, lineHeight: 1.55,
                  resize: 'vertical', outline: 'none',
                  boxShadow: '0 1px 0 rgba(0,0,0,0.02) inset' }} />
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  data-testid={`guidance-save-${e.id}`}
                  onClick={() => handleSaveEdit(e.id)}
                  disabled={savingId === e.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 8, border: 'none',
                    background: savingId === e.id
                      ? 'rgba(198,167,90,0.45)'
                      : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DK} 100%)`,
                    color: '#FFFFFF', fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.4px', textTransform: 'uppercase',
                    cursor: savingId === e.id ? 'wait' : 'pointer',
                    boxShadow: savingId === e.id
                      ? 'none'
                      : '0 4px 12px -4px rgba(198,167,90,0.45)',
                    transition: 'all 180ms ease-out' }}>
                  <Save size={13} strokeWidth={2.25} /> Save
                </button>
                <button onClick={handleCancelEdit}
                  style={{ display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 8,
                    border: `1px solid ${BORDER}`, background: SURFACE,
                    color: MUTED, fontSize: 11, fontWeight: 700,
                    letterSpacing: '0.4px', textTransform: 'uppercase',
                    cursor: 'pointer',
                    transition: 'all 180ms ease-out' }}>
                  <X size={13} strokeWidth={2.25} /> Cancel
                </button>
              </div>
            </>
          ) : (
            // ── Read view ───────────────────────────────────────────
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                flexWrap: 'wrap' }}>
                <Badge tone="neutral">{e.category || 'general'}</Badge>
                <span style={{ fontFamily: 'Cormorant Garamond, Georgia, serif',
                  fontSize: 16, fontWeight: 600, color: DARK,
                  letterSpacing: '0.15px', lineHeight: 1.2,
                  flex: 1, minWidth: 0 }}>
                  {e.title}
                </span>
                {e.is_active
                  ? <Badge tone="active">active</Badge>
                  : <Badge tone="inactive">inactive</Badge>}
              </div>
              <div style={{ fontSize: 12, color: DARK, lineHeight: 1.6,
                whiteSpace: 'pre-wrap', paddingLeft: 2 }}>
                {e.body}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6,
                paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>
                {e.is_active && (
                  <button
                    data-testid={`guidance-edit-${e.id}`}
                    onClick={() => handleStartEdit(e)}
                    style={{ padding: '6px 14px', borderRadius: 8,
                      border: `1px solid ${GOLD}`, background: 'transparent',
                      color: GOLD_DK, fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.5px', textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 180ms ease-out' }}>
                    Edit
                  </button>
                )}
                {e.is_active ? (
                  <button
                    data-testid={`guidance-delete-${e.id}`}
                    onClick={() => handleSoftDelete(e.id)}
                    disabled={savingId === e.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 5,
                      padding: '6px 14px', borderRadius: 8,
                      border: 'none', background: 'transparent',
                      color: MUTED, fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.5px', textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 180ms ease-out' }}>
                    <Trash2 size={11} strokeWidth={2} /> Soft-delete
                  </button>
                ) : (
                  <button
                    data-testid={`guidance-reactivate-${e.id}`}
                    onClick={() => handleReactivate(e.id)}
                    disabled={savingId === e.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 5,
                      padding: '6px 14px', borderRadius: 8,
                      border: `1px solid ${BORDER}`, background: SURFACE,
                      color: DARK, fontSize: 10, fontWeight: 700,
                      letterSpacing: '0.5px', textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 180ms ease-out' }}>
                    <RotateCcw size={11} strokeWidth={2} /> Reactivate
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default OscarGuidancePanel;
